import { Ionicons } from '@expo/vector-icons';
import { useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
} from 'react-native-reanimated';

import { ModernCard } from '@/components/shared/cards/ModernCard';
import { SummaryLoadingCard } from '@/components/home/SummaryLoadingCard';
import { InfoPopover } from '@/components/ui/InfoPopover';
import { SummaryWeatherRow } from '@/components/home/SummaryWeatherRow';
import { Spacing } from '@/constants/spacing';
import { Colors } from '@/constants/theme';
import { getBodyFont, getHeadingFont } from '@/constants/typography';
import { useColorScheme } from '@/hooks/use-color-scheme';
import type { HomeSummary, SummaryAction, SummarySection } from '@/types/home-summary';

type DaySummaryCardProps = {
  summary: HomeSummary | null;
  isLoading?: boolean;
  isRefreshing?: boolean;
  onActionPress?: (action: SummaryAction) => void;
  onReport?: (summary: HomeSummary) => void;
  userName?: string | { displayName: string; isLoading: boolean };
};

/**
 * DaySummaryCard renders the latest AI-generated home summary with CTAs.
 */
export function DaySummaryCard({
  summary,
  isLoading = false,
  isRefreshing = false,
  onActionPress,
  onReport,
  userName = 'there',
}: DaySummaryCardProps) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];
  const [isPopoverVisible, setIsPopoverVisible] = useState(false);
  const infoButtonRef = useRef(null);

  // Handle userName being either string or object
  const userNameData = typeof userName === 'string'
    ? { displayName: userName, isLoading: false }
    : userName;

  // Animated values for blur effect
  const opacity = useSharedValue(userNameData.isLoading ? 0.3 : 1);
  const scale = useSharedValue(userNameData.isLoading ? 0.98 : 1);

  useEffect(() => {
    if (!userNameData.isLoading) {
      opacity.value = withTiming(1, { duration: 200 });
      scale.value = withTiming(1, { duration: 200 });
    }
  }, [userNameData.isLoading, opacity, scale]);

  const animatedStyle = useAnimatedStyle(() => ({
    opacity: opacity.value,
    transform: [{ scale: scale.value }],
  }));

  const formatLastUpdated = (generatedAt: string) => {
    try {
      const date = new Date(generatedAt);

      const timeString = date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });

      const dateString = date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
      });

      return `Last updated:\n${timeString} ${dateString}`;
    } catch {
      return 'Last updated: N/A';
    }
  };

  const handleInfoPress = () => {
    if (!summary?.generatedAt) return;
    setIsPopoverVisible(true);
  };

  const handlePopoverClose = () => {
    setIsPopoverVisible(false);
  };

  const renderSection = (section: SummarySection) => {
    const isToday = section.title?.trim().toLowerCase() === 'today';
    const shouldShowTitle =
      Boolean(section.title) &&
      section.title!.trim().length > 0 &&
      !isToday;

    return (
      <View key={section.id} style={styles.sectionBlock}>
        {shouldShowTitle ? (
          <Text style={[styles.sectionTitle, { color: palette.text, fontFamily: getBodyFont() }]}>
            {section.title}
          </Text>
        ) : null}

        {section.body ? (
          <Text style={[styles.sectionBody, { color: palette.text, fontFamily: getBodyFont() }]}>
            {section.body}
          </Text>
        ) : null}

        {section.bullets?.length ? (
          <View style={styles.bulletList}>
            {section.bullets.map(bullet => (
              <View key={bullet.id} style={styles.bulletRow}>
                <View style={[styles.bulletDot, { backgroundColor: palette.tint }]} />
                <View style={styles.bulletContent}>
                  <Text style={[styles.bulletText, { color: palette.text, fontFamily: getBodyFont() }]}>
                    {bullet.text}
                  </Text>
                </View>
              </View>
            ))}
          </View>
        ) : null}
      </View>
    );
  };


  if (isLoading) {
    return <SummaryLoadingCard />;
  }

  return (
    <>
      <InfoPopover
        visible={isPopoverVisible}
        onClose={handlePopoverClose}
        message={summary?.generatedAt ? formatLastUpdated(summary.generatedAt) : ''}
        from={infoButtonRef}

      />
      <View style={styles.container}>
      <Animated.Text style={[styles.headerText, animatedStyle, { color: palette.text, fontFamily: getBodyFont(), fontWeight: '600' }]}>
        {userNameData.displayName ? `Hello, ${userNameData.displayName}!` : 'Hello!'}
      </Animated.Text>
      <View style={styles.subheadingRow}>
        <Text style={[styles.subheadingText, { color: palette.primary, fontFamily: getBodyFont() }]}>
          {isRefreshing ? 'Updating your brief...' : 'Your daily brief'}
        </Text>
        {isRefreshing && (
          <ActivityIndicator size="small" color={palette.primary} style={{ marginLeft: Spacing.xs }} />
        )}
        {summary?.generatedAt ? (
          <TouchableOpacity
            ref={infoButtonRef}
            onPress={handleInfoPress}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            style={styles.infoButton}>
            <Ionicons name="information-circle-outline" size={16} color={palette.textMuted} />
          </TouchableOpacity>
        ) : null}
      </View>

      <ModernCard variant="elevated" padding="lg">
        {!summary ? (
          <View style={styles.emptyState}>
            <Text style={[styles.emptyTitle, { color: palette.text, fontFamily: getHeadingFont() }]}>
              No summary yet
            </Text>
            <Text style={[styles.emptySubtitle, { color: palette.textMuted, fontFamily: getBodyFont() }]}>
              Enable Morning Brief or Evening Recap in Settings to see an AI summary here.
            </Text>
          </View>
        ) : null}

        {summary ? (
          <View style={styles.summaryContainer}>
            <SummaryWeatherRow variant={summary.variant} palette={palette} />
            {summary.summaryText ? (
              <Text style={[styles.summaryLead, { color: palette.text, fontFamily: getBodyFont() }]}>
                {summary.summaryText}
              </Text>
            ) : null}

            {summary.sections.map(renderSection)}

            {onReport ? (
              <TouchableOpacity
                onPress={() => onReport(summary)}
                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                style={styles.reportRow}>
                <Ionicons name="flag-outline" size={13} color={palette.textMuted} />
                <Text style={[styles.reportText, { color: palette.textMuted, fontFamily: getBodyFont() }]}>
                  Report this summary
                </Text>
              </TouchableOpacity>
            ) : null}
          </View>
        ) : null}
      </ModernCard>
    </View>
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    gap: Spacing.sm,
    paddingTop: Spacing.sm,
  },
  headerText: {
    fontSize: 22,
  },
  infoButton: {
    padding: Spacing.xxs,
    marginLeft: Spacing.xs,
  },
  subheadingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.xs,
    marginTop: Spacing.xs,
  },
  subheadingText: {
    fontSize: 14,
    fontWeight: '600',
  },
  card: {
    gap: Spacing.md,
  },
  emptyState: {
    gap: Spacing.sm,
  },
  emptyTitle: {
    fontSize: 14,
    fontWeight: '600',
  },
  emptySubtitle: {
    fontSize: 10,
    lineHeight: 16,
  },
  summaryContainer: {
    gap: Spacing.md,
  },
  summaryLead: {
    fontSize: 14,
    lineHeight: 22,
  },
  sectionBlock: {
    gap: Spacing.md,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: '700',
  },
  sectionBody: {
    fontSize: 14,
    lineHeight: 22,
  },
  bulletList: {
    gap: Spacing.sm,
  },
  bulletRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: Spacing.sm,
  },
  bulletDot: {
    width: 6,
    height: 6,
    borderRadius: 3, // circular
    marginTop: 7,
  },
  bulletContent: {
    flex: 1,
    gap: Spacing.xs,
  },
  bulletText: {
    fontSize: 14,
    lineHeight: 22,
  },
  reportRow: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: Spacing.xxs,
    marginTop: Spacing.xs,
  },
  reportText: {
    fontSize: 11,
    fontWeight: '500',
  },
  sectionCta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.xs,
  },
  sectionCtaText: {
    fontSize: 12,
    fontWeight: '600',
  },
});
